import { DateTime } from "luxon";

import { ITillReceipt } from "../../models/till/ITillReceipt";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";

export interface ReceiptTemplateProps {
  receipt: ITillReceipt;
  logoSrc?: string | null;
}

const tenderLabel: Record<string, string> = {
  Cash: "Cash",
  Card: "Card",
  BankTransfer: "Bank transfer",
};

/**
 * A complete HTML document for an 80mm thermal receipt. Pure: rendered with
 * renderToStaticMarkup and handed to printHtml, so no hooks or app styles here.
 */
export default function ReceiptTemplate({ receipt, logoSrc }: ReceiptTemplateProps) {
  const parts = receipt.lines.filter((l) => l.kind === "Part");
  const extras = receipt.lines.filter((l) => l.kind !== "Part");

  return (
    <html>
      <head>
        <meta charSet="utf-8" />
        <title>Receipt {receipt.reference ?? ""}</title>
        <style>{`
          @page { size: 80mm auto; margin: 4mm; }
          * { box-sizing: border-box; }
          body { width: 72mm; margin: 0; font: 12px/1.35 "Courier New", Courier, monospace; color: #000; }
          .centre { text-align: center; }
          .muted { font-size: 11px; }
          .rule { border-top: 1px dashed #000; margin: 6px 0; }
          .row { display: flex; justify-content: space-between; gap: 6px; }
          .row .desc { flex: 1; word-break: break-word; }
          .row .amt { white-space: nowrap; }
          .total { font-size: 15px; font-weight: bold; }
          .logo { max-width: 48mm; max-height: 18mm; margin: 0 auto 4px; display: block; }
          h1 { font-size: 14px; margin: 0 0 2px; }
          p { margin: 0; }
        `}</style>
      </head>
      <body>
        <div className="centre">
          {logoSrc ? <img className="logo" src={logoSrc} alt="" /> : null}
          <h1>{receipt.business?.name ?? ""}</h1>
          {receipt.business?.address?.map((line, i) => (
            <p className="muted" key={i}>{line}</p>
          ))}
          {receipt.business?.telephone ? <p className="muted">Tel: {receipt.business.telephone}</p> : null}
          {receipt.business?.vatNumber ? <p className="muted">VAT No: {receipt.business.vatNumber}</p> : null}
        </div>

        <div className="rule" />

        <div className="row"><span>Receipt</span><span className="amt">{receipt.reference ?? ""}</span></div>
        {receipt.invoiceNumber ? <div className="row"><span>Invoice</span><span className="amt">{receipt.invoiceNumber}</span></div> : null}
        <div className="row"><span>Date</span><span className="amt">{formatDateTime(receipt.soldAt, DateTime.DATETIME_SHORT)}</span></div>
        {receipt.operatorName ? <div className="row"><span>Served by</span><span className="amt">{receipt.operatorName}</span></div> : null}
        {receipt.accountName ? <div className="row"><span>Customer</span><span className="amt">{receipt.accountName}</span></div> : null}

        <div className="rule" />

        {parts.map((line, i) => (
          <div key={i}>
            <div className="row">
              <span className="desc">{line.description}</span>
              <span className="amt">{PriceFormat(line.lineTotal)}</span>
            </div>
            <p className="muted">
              {line.tagNumber ? `SKU ${line.tagNumber} · ` : ""}
              {line.isMarginScheme ? "Margin scheme" : line.taxRate > 0 ? `VAT ${line.taxRate}%` : "Zero rated"}
              {` · ex VAT ${PriceFormat(line.unitPrice)} · VAT ${PriceFormat(Math.max(0, line.lineTotal - line.unitPrice))}`}
            </p>
          </div>
        ))}

        {extras.length > 0 ? <div className="rule" /> : null}
        {extras.map((line, i) => (
          <div className="row" key={`x${i}`}>
            <span className="desc">{line.description}</span>
            <span className="amt">{PriceFormat(line.lineTotal)}</span>
          </div>
        ))}

        <div className="rule" />

        <div className="row"><span>Goods (ex VAT)</span><span className="amt">{PriceFormat(receipt.subTotal)}</span></div>
        <div className="row"><span>VAT</span><span className="amt">{PriceFormat(receipt.tax)}</span></div>
        {receipt.lines.some((l) => l.isMarginScheme) ? (
          <p className="muted">Items marked &ldquo;Margin scheme&rdquo; are sold under the VAT margin scheme; no VAT is shown or reclaimable on them.</p>
        ) : null}
        <div className="row total"><span>TOTAL</span><span className="amt">{PriceFormat(receipt.total)}</span></div>

        <div className="rule" />

        <div className="row">
          <span>Paid by {tenderLabel[receipt.tender.method] ?? receipt.tender.method}</span>
          <span className="amt">
            {receipt.tender.method === "Card" && receipt.tender.last4
              ? `${(receipt.tender.cardBrand ?? "card").toUpperCase()} •••• ${receipt.tender.last4}`
              : ""}
          </span>
        </div>
        {receipt.tender.method === "Cash" && receipt.tender.cashTendered != null ? (
          <>
            <div className="row"><span>Tendered</span><span className="amt">{PriceFormat(receipt.tender.cashTendered)}</span></div>
            <div className="row"><span>Change</span><span className="amt">{PriceFormat(receipt.tender.changeDue ?? 0)}</span></div>
          </>
        ) : null}

        {receipt.footer ? (
          <>
            <div className="rule" />
            <p className="centre muted">{receipt.footer}</p>
          </>
        ) : null}
      </body>
    </html>
  );
}
